import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import _ from "lodash";
import { useDispatch, useSelector, connect } from "react-redux";
import { reduxForm, Field, initialize, formValueSelector } from "redux-form";
import { openModal, closeModal, fetchError } from "../../actions";
import { Form, Button, Grid, Menu, Container, Header } from "semantic-ui-react";
import {
  TextInput,
  SelectInput,
  FileInput,
  AdvertiserOptions,
} from "../utils/form-fields";
import { ColorPicker } from "../utils/helpers";
import { createBanner, updateBanner, uploadFile } from "../../firebase";
import { CancelButton } from "../utils/buttons";
import Loading from "../../app/loading";

let templateFields = [];

let DefaultForm = ({
  change,
  submitting,
  pristine,
  handleSubmit,
  invalid,
  retailer,
  fieldValues,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const banner = useSelector((state) => state.banner);
  const advertisers = useSelector((state) => state.advertisers || []);
  const user = useSelector((state) => state.user);
  const template = useSelector((state) => state.template);

  const [showLoading, setShowLoading] = useState(true);

  //const presetColors = useRef();

  const [colorPickerPresets, setColorPickerPresets] = useState();

  useEffect(() => {
    if (template.variables) {
      banner.fields = banner.fields || {};
      template.variables.forEach((variable) => {
        if (variable.defaultValue) {
          let value = banner.fields[variable.uniqueName];
          banner.fields[variable.uniqueName] = value || variable.defaultValue;
        } else {
          if (!banner.fields[variable.uniqueName])
            banner.fields[variable.uniqueName] = "";
        }
      });
      dispatch(initialize("defaultForm", banner));
    }
    //setShowLoading(false);
  }, [dispatch, banner, template]);

  useEffect(() => {
    templateFields = template.variables;
  }, [template]);

  useEffect(() => {
    if (template.colorPresets) {
      setColorPickerPresets(template.colorPresets);
    }
    setShowLoading(false);
  }, [template, setColorPickerPresets]);

  const getAdvertiser = (advertiser) => {
    return _.find(advertisers, (item) => {
      return item.displayName === advertiser;
    });
  };

  const getOptions = (options) => {
    let choices = [];
    if (!options) return choices;
    options.map((option) => {
      return (choices = [
        ...choices,
        { text: option.value, value: option.value, key: option.value },
      ]);
    });
    return choices;
  };

  const getComponent = (field) => {
    const attr = field.attributes["xsi:type"];
    let Comp;
    switch (attr) {
      case "StringCreativeTemplateVariable":
        Comp = TextInput;
        break;
      case "UrlCreativeTemplateVariable":
        Comp = TextInput;
        break;
      case "ListStringCreativeTemplateVariable":
        Comp = SelectInput;
        break;
      default:
        Comp = TextInput;
    }
    return Comp;
  };

  const handleFileUpload = (file, key, title, size) => {
    if (!size) size = "";
    let folder = banner.key || key;
    if (file.type.indexOf("image/") === -1) {
      return dispatch(
        fetchError({
          name: "Upload error",
          error: "Wrong file type. Please upload an image - jpg, gif or png",
        })
      );
    }
    uploadFile(file, folder, (data) => {
      dispatch(
        openModal("ProgressModal", {
          size: "tiny",
          progress: data.progress,
          name: file.name,
          title,
          centered: false,
        })
      );
      if (data.progress === 100) {
        change(key, {
          name: data.name,
          path: data.path,
          size: size,
        });
        //window.setTimeout(() => dispatch(closeModal()), 1000);
        dispatch(closeModal());
      }
      if (data.error) {
        dispatch(
          openModal("ErrorModal", {
            size: "tiny",
            centered: false,
            error: {
              name: file.name,
              error: "Image error",
              reason: "File size too large or dimensions are wrong",
            },
          })
        );
      }
    });
  };

  const handleSubmitForm = async (values) => {
    setShowLoading(true);
    let advertiserId = await getAdvertiser(values.advertiser);
    advertiserId = advertiserId.advertiserId;
    let templateId = template.id;
    let bespokeTemplate = false;
    let templateType = template.type;
    let bannerType = banner.bannerType || template.name;
    let currentUser = `${user.displayName} (${user.organisation})`;
    const submitValues = {
      ...values,
      bannerType,
      currentUser: false,
      retailer,
      advertiserId,
      templateId,
      bespokeTemplate,
      templateType,
    };
    submitValues.lastModBy = currentUser;
    //return console.log(submitValues);
    banner.key
      ? updateBanner(submitValues, banner.key, dispatch).then((message) => {
          setShowLoading(false);
        })
      : createBanner(submitValues, dispatch)
          .then((message) => {
            if (message.id) navigate(`/${retailer}/banner/${message.id}`);
            console.log(message);
          })
          .catch((error) => console.log(error));
  };

  if (showLoading) return <Loading />;

  return (
    <Form onSubmit={handleSubmit(handleSubmitForm)}>
      <Grid padded className="trade-form">
        <Grid.Row>
          <Grid.Column computer={4} tablet={8} mobile={16}>
            <Header as="h3">Banner info</Header>
            <Field
              name="bannerTitle"
              label="Title"
              type="text"
              component={TextInput}
              placeholder="Add a banner title"
              isRequired={true}
            />
            <Field
              name="adNumber"
              label="Ad number"
              type="text"
              component={TextInput}
              placeholder="Add an ad number"
              isRequired={true}
            />
            <div className="field">
              <div className="field">
                <label htmlFor="advertiser">Advertiser</label>
                <Field
                  name="advertiser"
                  component="select"
                  multiple={false}
                  className="ui selection dropdown"
                >
                  {!user.restrictByAdvertiser && (
                    <option value="">Choose an advertiser</option>
                  )}
                  <AdvertiserOptions advertisers={advertisers} />
                </Field>
              </div>
            </div>
            <Field
              name="bannerURL"
              label="Banner link"
              type="text"
              component={TextInput}
              placeholder="Add a URL link"
              isRequired={true}
            />
          </Grid.Column>
          <Grid.Column computer={4} tablet={8} mobile={16}>
            <Header as="h3">Template fields</Header>
            {_.map(
              template.variables,
              (field) =>
                field.attributes["xsi:type"] !==
                  "AssetCreativeTemplateVariable" && (
                  <Field
                    options={getOptions(field.choices)}
                    label={field.label}
                    name={`fields.${field.uniqueName}`}
                    key={`fields.${field.uniqueName}`}
                    type="text"
                    component={getComponent(field)}
                    isRequired={field.isRequired}
                  >
                    {field.colorPicker && (
                      <ColorPicker
                        presetColors={colorPickerPresets}
                        color={fieldValues[field.uniqueName]}
                        colorPickerType={field.colorPickerType}
                        onSelect={(col) => {
                          change(`fields.${field.uniqueName}`, col);
                        }}
                      />
                    )}
                  </Field>
                )
            )}
          </Grid.Column>
          <Grid.Column computer={4} tablet={8} mobile={16}>
            <Header as="h3">Images</Header>
            {_.map(
              template.variables,
              (field) =>
                field.attributes["xsi:type"] ===
                  "AssetCreativeTemplateVariable" && (
                  <div className="field" key={field.uniqueName}>
                    <label>{field.label}</label>
                    {fieldValues[field.uniqueName] &&
                      fieldValues[field.uniqueName].path && (
                        <img
                          src={fieldValues[field.uniqueName].path}
                          alt={field.label}
                        />
                      )}
                    <Field
                      name={`fields.${field.uniqueName}`}
                      component={FileInput}
                      fileName={
                        fieldValues[field.uniqueName] &&
                        fieldValues[field.uniqueName].name
                          ? fieldValues[field.uniqueName].name
                          : ""
                      }
                      callback={(file) =>
                        handleFileUpload(
                          file,
                          `fields.${field.uniqueName}`,
                          field.label,
                          field.size
                        )
                      }
                      handleFileRemove={(e) =>
                        change(`fields.${field.uniqueName}`, {})
                      }
                    />
                  </div>
                )
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Menu fixed="bottom" size="mini">
        <Container>
          <Menu.Item>
            <Button
              disabled={submitting || pristine || invalid || showLoading}
              positive
              type="submit"
              loading={showLoading}
              size="mini"
            >
              Save
            </Button>
          </Menu.Item>
          <Menu.Item>
            <CancelButton
              id={banner.key}
              location={location}
              retailer={retailer}
              pristine={pristine}
              size="mini"
            />
          </Menu.Item>
        </Container>
      </Menu>
    </Form>
  );
};

const max = (value, limit) => {
  return value > limit ? `Please limit to ${limit} characters` : null;
};

const validate = (values) => {
  const isUrlValid = (url) => {
    return url.match(
      /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g
    );
  };

  const hasWhiteSpace = (hex) => {
    return hex.match(/\s/g);
  };

  const errors = { fields: {} };

  if (!values.bannerTitle) {
    errors.bannerTitle = "Please enter a title";
  }
  if (!values.adNumber || isNaN(Number(values.adNumber))) {
    errors.adNumber = "Please enter a number only";
  }
  if (!values.advertiser) {
    errors.advertiser = "Please select an advertiser";
  }
  if (values.bannerURL) {
    if (!isUrlValid(values.bannerURL)) {
      errors.bannerURL = "Please enter a valid URL";
    }
    if (isUrlValid(values.bannerURL)) {
      if (hasWhiteSpace(values.bannerURL)) {
        errors.bannerURL = "Please remove any spaces";
      }
    }
  }

  templateFields.forEach((field) => {
    if (field.isRequired) {
      if (values.fields) {
        if (!values.fields[field.uniqueName]) {
          errors.fields[field.uniqueName] = "This is a required field";
        }
      }
    }
  });

  return errors;
};

const warn = (values) => {
  const warnings = { fields: {} };

  templateFields.forEach((field) => {
    if (field.max) {
      if (values.fields) {
        if (values.fields[field.uniqueName]) {
          let msg = max(values.fields[field.uniqueName].length, field.max);
          if (msg) warnings.fields[field.uniqueName] = msg;
        }
      }
    }
  });

  return warnings;
};

DefaultForm = reduxForm({
  validate,
  form: "defaultForm",
  warn,
})(DefaultForm);

const selector = formValueSelector("defaultForm");
let fieldValues = {};
DefaultForm = connect((state) => {
  const advertiserValue = selector(state, "advertiser");
  if (state.template.variables) {
    state.template.variables.map((field) => {
      return (fieldValues = {
        ...fieldValues,
        [field.uniqueName]: selector(state, `fields.${field.uniqueName}`),
      });
    });
  }
  return {
    advertiserValue,
    fieldValues,
  };
})(DefaultForm);

export default DefaultForm;
